import { useState } from "react";
import styles from "../ReviewsSectionStyles.module.scss";
import WhiteStarIcon from "@Icons/WhiteStarIcon";
import Image from "@Components/Image";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import { transformVideoPath, updateCloudinaryUrl } from "../data/helper";
import ReviewVideoThumbnail from "./ReviewVideoThumbnail";
import VideoReviewCard from "./VideoReviewCard";
import MobileVideoReviewCard from "./MobileVideoReviewCard";
import { GUMLET_BASE_URL } from "constants/index";
import useTranslationHook from "hooks/useTranslationHook";

const ReviewCard = ({
  showAsList = false,
  review,
  handleMediaClick,
  getTimeText,
  isDesktop = false,
  variant,
}: any) => {
  const [showFull, setShowFull] = useState<boolean>(false);
  const { t } = useTranslationHook();
  const handleToggleReview = () => {
    setShowFull((val) => !val);
  };

  if (variant === "video") {
    return <VideoReviewCard review={review} getTimeText={getTimeText} />;
  }

  if (variant === "videoMobile") {
    return <MobileVideoReviewCard review={review} />;
  }

  const reviewVideos: any = [];
  review?.videos?.forEach((video: any) => {
    let newVideo = { ...video };
    if (
      !video?.path?.startsWith(GUMLET_BASE_URL) ||
      !video?.thumbnail_url?.startsWith(GUMLET_BASE_URL)
    ) {
      newVideo = {
        ...video,
        category: "videos",
        path: transformVideoPath(video?.path),
        thumbnail_url: updateCloudinaryUrl(transformVideoPath(video?.thumbnail_url), 200, 600),
      };
    }
    reviewVideos.push(newVideo);
  });

  const imgLimit = isDesktop ? 5 : 4;
  const charLimit = isDesktop ? 180 : 140;
  const reviewContent = review?.content?.trim() || "";
  const isLongReview = reviewContent.length > charLimit;
  const displayedContent =
    !isLongReview || showFull ? reviewContent : `${reviewContent.slice(0, charLimit)}...`;

  return (
    <div
      className={formatClassNames(
        showAsList ? styles.reviewCardOuterListItem : styles.reviewCardOuter,
        isDesktop && styles.desktop,
      )}
    >
      <div className={styles.reviewCardTop}>
        <div className={styles.reviewCardTopLhs}>
          <div className={styles.initialDiv}>
            {review?.profile_picture?.path ? (
              <Image height="inherit" width="inherit" src={review.profile_picture.path} />
            ) : (
              review?.user_details?.name?.split(" ")?.map((name: string) => name[0])
            )}
          </div>
          <div data-testid="property-review-user-name" className={styles.reviewerName}>
            {review?.user_details?.name}
          </div>
        </div>
        <div className={styles.reviewCardTopRhs}>
          <div className={styles.ratingDiv}>
            {parseFloat(review.rating).toFixed(1)}
            <span className={styles.starIconWrapper}>
              <WhiteStarIcon size="16" />
            </span>
          </div>
          {/* <div>{getTimeText(review)}</div> */}
        </div>
      </div>
      <div data-testid="property-review-text" className={styles.reviewCardContent}>
        {displayedContent}
        {isLongReview && (
          <span
            onClick={handleToggleReview}
            role="none"
            className={formatClassNames(styles.showFull, isDesktop && styles.desktop)}
            data-testid={
              showFull
                ? t("propertypage.reviewssection.readless")
                : t("propertypage.reviewssection.readmore")
            }
          >
            {" "}
            {showFull
              ? t("propertypage.reviewssection.readless")
              : t("propertypage.reviewssection.readmore")}
          </span>
        )}
      </div>
      <div className={styles.reviewMedia} role="none">
        {reviewVideos?.slice(0, imgLimit).map((img: any, index: number) => (
          <ReviewVideoThumbnail
            key={`ReviewCardVideo${index}`}
            img={img}
            index={index}
            isDesktop={isDesktop}
            review={review}
            handleMediaClick={() => handleMediaClick(img.index, "review_video_viewed")}
          />
        ))}
        {reviewVideos.length < imgLimit ? (
          review?.images
            .slice(0, imgLimit - reviewVideos?.length)
            .map((img: any, index: number) => (
              <div
                className={styles.mediaOverlayContainer}
                role="none"
                onClick={() => handleMediaClick(img.index, "review_image_viewed")}
                key={`ReviewCardImage${index}`}
              >
                <Image
                  className={formatClassNames(styles.topThumbnailsImg, isDesktop && styles.desktop)}
                  src={img.path}
                  height={64}
                  width={64}
                />
                {index + 1 == imgLimit - (reviewVideos?.length || 0) &&
                review?.images.length - (imgLimit - (reviewVideos?.length || 0)) > 0 ? (
                  <div className={styles.mediaOverlay}>
                    <div className={styles.plusText}>
                      +{review?.images.length - (imgLimit - (reviewVideos?.length || 0))}
                    </div>
                  </div>
                ) : (
                  <></>
                )}
              </div>
            ))
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default wrapperHOC(ReviewCard, {
  componentName: "ReviewCard-ReviewsSection-PropertyPage",
  showForChina: true,
});
